import { formatPhone, isPhoneNumber } from '@bybeam/formatting';
import { Address, PaymentStatus } from '@bybeam/platform-types';
import { GeocoderResult } from '@platform-ui-common/hooks/useGeocoder.js';
import dayjs from 'dayjs';

const currencyOptions = {
  style: 'currency',
  currency: 'USD',
};
/**
 * Reason why we have two currency formatters:
 * Creating instances of these Intl formats is an expensive operation
 * The APIs are designed such that developers should re-use format instances instead of always creating new ones.
 */
const currencyFormatter = new Intl.NumberFormat('en-US', {
  ...currencyOptions,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const currencyFormatterWithCents = new Intl.NumberFormat('en-US', currencyOptions);

export function formatCurrency(valueInCents: number, withCents?: boolean): string {
  const formatter = withCents ? currencyFormatterWithCents : currencyFormatter;
  return formatter.format(valueInCents / 100);
}

export function formatNumber(value: number): string {
  return value.toLocaleString();
}

const percentageFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 0,
});

// value is the decimal percent, so 0 = 0%, 1 = 100%
export function formatPercentage(value: number): string {
  return percentageFormatter.format(value);
}

export function formatAddress(address: Partial<Address>): string {
  return address
    ? `${address.addressLine1 || ''}${address.addressLine2 ? ` ${address.addressLine2}` : ''}${
        address.addressLine1 ? ',' : ''
      } ${address.city}, ${address.state} ${address.zip}`
    : '';
}

export function formatCityLabel(value: GeocoderResult): string {
  return `${value?.address?.state ? `${value.address.city}, ${value.address.state}` : ''}`;
}

// formats the string provided if it can be formatted as a phone number; otherwise
// just returns the provided string, unformatted.
export function formatIfPhone(rawContact?: string | number): string | undefined {
  if (!rawContact) return undefined;

  const contact = `${rawContact}`;
  if (!isPhoneNumber(contact)) return contact;
  return formatPhone(contact);
}

export enum DateFormat {
  ShortDate,
  Timestamp,
  Slashes,
  DateTime,
  ShortSlashes,
  DashDate,
}

// Dayjs defaults to the browser's locality. Dates should be coming from the API in UTC, and formatted here
// for display in the local timezone. In certain circumstances, we need to display a timestamp from the database
// as a simple date. In this case, the timestamp is trimmed off the datestring before parsing to prevent the UTC
// to local timezone conversion from reverting to the prior day.
export function formatDate(
  rawDate: Date | string,
  format = DateFormat.Timestamp,
  ignoreTimestamp = false,
): string {
  let date = rawDate;
  if (!date) return undefined;
  if (ignoreTimestamp)
    date = (rawDate instanceof Date ? rawDate.toISOString() : rawDate)?.split('T')?.[0] || rawDate;
  switch (format) {
    case DateFormat.ShortDate:
      return dayjs(date).format('MMM D, YYYY');
    case DateFormat.ShortSlashes:
      return dayjs(date).format('MM/DD/YY');
    case DateFormat.Slashes:
      return dayjs(date).format('MM/DD/YYYY');
    case DateFormat.DateTime:
      return `${dayjs(date).format('M/D/YYYY')} at ${dayjs(date).format('h:mm A')}`;
    case DateFormat.DashDate:
      return dayjs(date).format('YYYY-MM-DD');
    default:
      return dayjs(date).format('MM/DD/YYYY h:mmA');
  }
}

export function formatPaymentStatuses(status: PaymentStatus, none = 'N/A'): string {
  switch (status) {
    case PaymentStatus.Success:
    case PaymentStatus.Initiated:
      return 'Paid';
    case PaymentStatus.Failed:
      return 'Failed';
    case PaymentStatus.Authorized:
      return 'Unclaimed';
    default:
      return none;
  }
}

export const formatInitials = (fullName: string): string => {
  return !fullName
    ? ''
    : fullName
        .split(' ')
        .reduce((acc: string, str: string) => (str[0] !== undefined ? acc + str[0] : acc), '');
};

export const camelCaseToSpace = (str: string): string => str.replace(/([A-Z])/g, ' $1');

export const mask = (str: string, start?: number, end?: number): string =>
  str ? `• • • • • ${str.slice(start ?? -4, end)}` : '';

export enum Mask {
  SSN = '___-__-____',
  EIN = '__-_______',
}
